/**
 * Client for /api/chat/runs (backend/chainlit/run_states.py).
 *
 * Which of the user's conversations are answering, waiting their turn,
 * waiting for the user's choice, ready, or never finished. An answer keeps
 * running when the user leaves its thread, so this is how the sidebar knows.
 *
 * A module store, like the notices bell: one poll for the whole page, and
 * subscribers re-render only when what they read actually changed — the chat
 * renders long markdown tables, and re-rendering every message on every poll
 * would cost more than the poll itself.
 */
import { useSyncExternalStore } from 'react';

export type RunState =
  | 'running'
  | 'queued'
  | 'needs_choice'
  | 'done'
  | 'failed';

export interface RunInfo {
  state: RunState;
  title: string;
}

interface Store {
  runs: Record<string, RunInfo>;
  loaded: boolean;
}

export const ACTIVE_STATES: ReadonlySet<string> = new Set([
  'running',
  'queued',
  'needs_choice'
]);

export const isActive = (state?: string) => !!state && ACTIVE_STATES.has(state);

let state: Store = { runs: {}, loaded: false };
let signature = '';
const listeners = new Set<() => void>();

function set(next: Store) {
  const nextSignature = JSON.stringify(next);
  if (nextSignature === signature) return;
  signature = nextSignature;
  state = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const getRuns = () => state;

export const useRuns = () => useSyncExternalStore(subscribe, () => state);

/** One thread's state; re-renders only when that state changes. */
export const useRunState = (threadId?: string | null) =>
  useSyncExternalStore(subscribe, () =>
    threadId ? state.runs[threadId]?.state : undefined
  );

export const useRunsLoaded = () =>
  useSyncExternalStore(subscribe, () => state.loaded);

let inflight: Promise<void> | null = null;

/** Re-read the states. A failure keeps what we last had. */
export function refreshRuns(): Promise<void> {
  if (inflight) return inflight;
  inflight = fetch('/api/chat/runs', { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null))
    .then((d: { runs: Record<string, RunInfo> } | null) => {
      if (d && d.runs) set({ runs: d.runs, loaded: true });
    })
    .catch(() => undefined)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** The user opened a thread whose answer was ready (or never came). */
export function markRunSeen(threadId: string) {
  const current = state.runs[threadId];
  if (!current || isActive(current.state)) return;
  const runs = { ...state.runs };
  delete runs[threadId];
  set({ ...state, runs });
  fetch(`/api/chat/runs/${encodeURIComponent(threadId)}/seen`, {
    method: 'POST',
    credentials: 'include'
  }).catch(() => undefined);
}
